import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  EMPTY,
  Observable,
  catchError,
  delay,
  filter,
  map,
  of,
  switchMap,
  take,
  tap,
  throwError,
  zip,
} from 'rxjs';
import { DeliveryLocation } from 'src/app/api/contracts/location';
import { WeekDay } from 'src/app/api/contracts/menu';
import {
  CreateOrderReq,
  OrderPaymentStatus,
  OrderType,
} from 'src/app/api/contracts/order';
import {
  RadioGroupValueChangeEvent,
  RadioOption,
  RadioOptionId,
} from 'src/app/components/UI/radio-group/radio-group.types';
import { CartService } from 'src/app/services/cart.service';
import { ClientService } from 'src/app/services/client.service';
import { FullscreenModalService } from 'src/app/services/fullscreen-modal.service';
import { MealMenuService } from 'src/app/services/meal-menu.service';
import { OrderService } from 'src/app/services/order.service';
import { ToastService } from 'src/app/services/toast.service';
import { Cart, CartItem } from 'src/app/types/cart';
import { LoaderController } from 'src/app/utils/loader-controller';
import { PaymentMethod } from 'src/app/types/payment';
import { PriceLabelComponent } from 'src/app/components/UI/price-label/price-label.component';
import { PaymentService } from 'src/app/services/payment.service';
import { ClientSubsidy } from 'src/app/api/contracts/client';
import { fadeInOutAnimation } from 'src/app/animations/fade-in-out.animation';
import { UserGuideService } from 'src/app/services/user-guide.service';
import { NotificationBlockStyle } from 'src/app/components/notification-block/notification-block.types';
import { InStoreProductsService } from 'src/app/services/in-store-products.service';
import { InStoreSession } from 'src/app/types/in-store';
import { StorageKeys, StorageService } from 'src/app/services/storage.service';
import { ModalService } from 'src/app/services/modal.service';
import { GenericModalType } from 'src/app/types/modals';
import { FlagsService } from 'src/app/services/flags.service';

@Component({
  selector: 'app-order-summary',
  templateUrl: './order-summary.component.html',
  styleUrls: ['./order-summary.component.scss'],
  animations: [fadeInOutAnimation()],
})
export class OrderSummaryComponent implements OnInit, AfterViewInit {
  public readonly cartItems$: Observable<CartItem[]>;
  public readonly cart$: Observable<Cart>;
  public readonly deliveryDays$: Observable<Set<WeekDay> | null>;
  public readonly deliveryLocation$: Observable<DeliveryLocation>;
  public readonly instoreSessionLocation$: Observable<DeliveryLocation | null>;
  public readonly paymentMethods = PaymentMethod;
  public readonly shippingPrice$: Observable<number> = of(0);
  public readonly ORDER_TYPES = OrderType;
  public readonly subtotal$: Observable<number> = of(0);
  public readonly total$: Observable<number> = of(0);
  public readonly weeksNumbersInOrder$: Observable<Set<number>>;
  public paymentMethodOptions$: Observable<RadioOption<PaymentMethod>[]> = of(
    []
  );
  public userHasCard$: Observable<boolean | null>; // should be readonly later
  public selectedPaymentMethodOptionId: RadioOptionId = 0;
  public selectedPaymentMethod: PaymentMethod = PaymentMethod.CARD;
  public clientSubsidy$: Observable<ClientSubsidy | null> = of(null);
  public shouldShowScrollFloatHint: boolean = false;
  public readonly notificationBlockStyles = NotificationBlockStyle;

  public readonly loader = new LoaderController();

  @ViewChild('subsidyAmountTemplate')
  subsidyAmountTemplate!: TemplateRef<PriceLabelComponent>;
  @ViewChild('wrapComponent')
  contentWrapComponent!: ElementRef<HTMLDivElement>;

  constructor(
    private readonly navCtrl: NavController,
    private readonly fullscreenModalService: FullscreenModalService,
    public readonly cartService: CartService,
    private readonly clientService: ClientService,
    private readonly orderService: OrderService,
    private readonly toastService: ToastService,
    private readonly menuService: MealMenuService,
    private readonly translateService: TranslateService,
    private readonly paymentService: PaymentService,
    private readonly userGuideService: UserGuideService,
    private readonly instoreService: InStoreProductsService,
    private readonly storageService: StorageService,
    private readonly modalService: ModalService,
    public readonly flagService: FlagsService
  ) {
    this.cartItems$ = this.cartService.currentCart$.pipe(
      map((cart) => cart.items)
    );

    this.cart$ = this.cartService.currentCart$;

    this.deliveryDays$ = this.cart$.pipe(
      map((cart) => {
        if (cart.orderType === OrderType.DELIVERY) {
          return new Set<WeekDay>(
            cart.items.map((item) => item.day as WeekDay)
          );
        }
        return null;
      })
    );

    this.deliveryLocation$ = this.clientService.clientLocation$;
    this.instoreSessionLocation$ =
      this.instoreService.currentInstoreSession$.pipe(
        switchMap((session) => {
          if (!session) {
            return this.storageService.get<DeliveryLocation>(
              StorageKeys.LAST_USED_INSTORE_LOCATION
            );
          }
          return of(session.location);
        })
      );

    this.shippingPrice$ = this.cartService.shippingPrice$;

    this.total$ = this.cartService.totalPrice$;

    this.weeksNumbersInOrder$ = this.cart$.pipe(
      map((cart) => cart.items.map((item) => item.weekNumber!)),
      map((weekNumbers: Array<number>) => new Set(Object.values(weekNumbers)))
    );

    this.userHasCard$ = this.clientService.clientCard$.pipe(
      map((card) => {
        if (card === null) {
          return null;
        }
        return !!card;
      })
    );

    this.clientSubsidy$ = this.cart$.pipe(
      switchMap((cart) => {
        let locationHashSource: Observable<DeliveryLocation>;
        if (cart.orderType === OrderType.DELIVERY) {
          locationHashSource = this.clientService.clientLocation$;
        }
        if (cart.orderType === OrderType.IN_STORE) {
          locationHashSource = this
            .instoreSessionLocation$ as Observable<DeliveryLocation>;
        }

        return locationHashSource!.pipe(
          switchMap((clientLocation) =>
            this.clientService.clientSubsidies$.pipe(
              map((subsidies) => {
                const subsidy = subsidies.find((s) =>
                  s.locations.some((l) => l.hash === clientLocation.hash)
                );
                return subsidy || null;
              })
            )
          )
        );
      })
    );

    this.shouldShowScrollFloatHint = true; // todo - add user guide service; check storage
  }

  ngOnInit() {
    return;
  }

  ngAfterViewInit() {
    this.loadPaymentMethods();
    this.setIfScrollFloatHintShouldAppear();
  }

  ionViewDidEnter() {
    this.clientService.retrieveClientCard().subscribe();
  }

  onEditClick(): void {
    this.navCtrl.pop().then((canGoBack) => {
      if (!canGoBack) {
        this.navCtrl.navigateRoot('cart');
      }
    });
  }

  onAddCreditCardClick(): void {
    this.clientService
      .openCardEditingDashboard()
      .pipe(switchMap(() => this.clientService.retrieveClientCard()))
      .subscribe((res) => {
        if (!res || res.lastFour) {
          return;
        }
        this.toastService.showNotification({
          message: this.translateService.instant('@common.creditCardAdded'),
        });
        this.userHasCard$ = of(true);
      });
  }

  onConfirmClick(): void {
    this.flagService.flags$
      .pipe(
        take(1),
        switchMap((flags) => {
          if (!flags?.appEnabled) {
            return this.modalService
              .showGenericInfoPopup({
                header: this.translateService.instant(
                  '@common.errors.theAppIsDisabled.header'
                ),
                message: this.translateService.instant(
                  '@common.errors.theAppIsDisabled.message'
                ),
                type: GenericModalType.ERROR,
              })
              .pipe(switchMap(() => EMPTY));
          }
          return this.cartService.currentCart$.pipe(take(1));
        }),
        switchMap((cart: Cart) => {
          this.loader.enqueue();
          if (!cart) return EMPTY;
          return (
            cart.orderType === OrderType.DELIVERY
              ? this.clientService.clientLocation$
              : this.instoreSessionLocation$
          ).pipe(
            map((location) => [cart, location] as [Cart, DeliveryLocation])
          );
        }),
        switchMap(([cart, location]: [Cart, DeliveryLocation]) => {
          const orderReq: CreateOrderReq =
            this.orderService.createOrderRequestObjectFromCart(
              cart,
              this.selectedPaymentMethod,
              location.hash
            );
          return zip([
            this.orderService.postNewOrder(orderReq),
            this.fullscreenModalService.showPaymentProcessing(),
          ]);
        }),
        switchMap(([order, paymentProcessingModal]) => {
          paymentProcessingModal.dismiss().subscribe();
          return this.fullscreenModalService.showOrderConfirmation(order);
        }),
        catchError((err) => {
          this.fullscreenModalService.dismiss();
          this.loader.dequeue();
          const isCardPaymentError =
            err.error.detail === 'CAPTURE_FAILED' ||
            err.error.detail === 'AUTHORIZATION_FAILED';
          if (isCardPaymentError) {
            this.modalService
              .showGenericInfoPopup({
                header: this.translateService.instant(
                  '@common.paymentError.header'
                ),
                message: this.translateService.instant(
                  '@common.paymentError.message'
                ),
                type: GenericModalType.ERROR,
              })
              .subscribe();
            return EMPTY;
          }
          this.toastService
            .showNotification({
              message: this.translateService.instant(
                '@common.errors.somethingWentWrong'
              ),
            })
            .subscribe();
          return throwError(() => err);
        }),
        switchMap((confirmationModal) => confirmationModal.onDismiss()),
        take(1)
      )
      .subscribe(() => {
        this.cartService.clearCart();
        this.navCtrl
          .navigateRoot('/store', {
            animated: true,
          })
          .then(() => {
            this.loader.dequeue();
          });
      });
  }

  onPaymentMethodSelected(
    event: RadioGroupValueChangeEvent<PaymentMethod>
  ): void {
    this.selectedPaymentMethodOptionId = event.id;
    this.selectedPaymentMethod = event.value;
  }

  scrollPageToBottom(target: HTMLElement): void {
    target.scrollTo({ top: target.clientHeight, behavior: 'smooth' });
  }

  private loadPaymentMethods(): void {
    this.loader.enqueue();
    this.paymentMethodOptions$ = this.paymentService.getPaymentMethods().pipe(
      map((paymentMethods) =>
        this.paymentService.getPaymentMethodsRadioOptions(
          paymentMethods,
          this.subsidyAmountTemplate
        )
      ),
      tap((paymentMethods) => {
        if (paymentMethods.length > 0) {
          this.selectedPaymentMethodOptionId = paymentMethods[0].id;
          this.selectedPaymentMethod = paymentMethods[0].value;
        }
        this.loader.dequeue();
      })
    );
  }

  private setIfScrollFloatHintShouldAppear(): void {
    this.cartItems$
      .pipe(
        filter((x) => !!x),
        take(1),
        delay(100)
      )
      .subscribe(() => {
        const wrapper = this.contentWrapComponent.nativeElement;
        this.shouldShowScrollFloatHint =
          this.userGuideService.scrollFloatHintShouldAppear(wrapper);
      });
  }
}
